import React, { useState, useRef, useEffect, useCallback, Component } from 'react';
import { useSpreadsheetEditor } from '../hooks/useSpreadsheetEditor';
import { Workbook } from '@fortune-sheet/react';
import '@fortune-sheet/react/dist/index.css';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

/**
 * SpreadsheetEditorPanel - Fortune Sheet editor panel
 *
 * Desktop: right-side resizable panel
 * Touch/Mobile: fullscreen overlay
 * Same pattern as PdfViewerPanel and DiagramEditorPanel.
 */

// Error boundary to catch Fortune Sheet internal formula bar errors
class SheetErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error) {
    // Suppress setCaretPosition errors from Fortune Sheet formula bar
    if (error?.message?.includes("reading '0'")) {
      this.setState({ hasError: false });
    }
  }
  render() { return this.props.children; }
}

const MIN_WIDTH = 500;
const DEFAULT_WIDTH = 700;
const STORAGE_KEY = 'spreadsheetEditorPanelWidth';

const SpreadsheetEditorPanel = () => {
  const { isOpen, sheetData, sheetTitle, close, key: sheetKey } = useSpreadsheetEditor();

  const getSavedWidth = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  };

  const [width, setWidth] = useState(getSavedWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const workbookRef = useRef(null);
  const workbookApiRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const latestDataRef = useRef(null);
  const formulaCellsRef = useRef([]);  // [{r, c, formula}]
  const recalcTimerRef = useRef(null);
  const isRecalcRef = useRef(false);

  // Detect touch devices
  useEffect(() => {
    const checkTouch = () => {
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const small = window.innerWidth < 768;
      setIsTouch(touch || small);
    };
    checkTouch();
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  // Slide animation
  useEffect(() => {
    if (isOpen && sheetData) {
      setShouldRender(true);
      latestDataRef.current = sheetData;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, sheetData]);

  // Force Fortune Sheet to recalculate layout after container is settled (Safari fix)
  // Multiple triggers to ensure Safari has settled its viewport
  useEffect(() => {
    if (isVisible && shouldRender) {
      const timers = [100, 300, 600].map(delay =>
        setTimeout(() => window.dispatchEvent(new Event('resize')), delay)
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [isVisible, shouldRender]);

  // Save width to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, width.toString());
  }, [width]);

  // Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isOpen && isTouch) {
      document.body.classList.add('viewer-open');
    } else {
      document.body.classList.remove('viewer-open');
    }
    return () => { document.body.classList.remove('viewer-open'); };
  }, [isOpen, isTouch]);

  // Export as CSV
  const exportCsv = () => {
    if (!latestDataRef.current) return;
    const data = latestDataRef.current;
    const sheet = Array.isArray(data) ? data[0] : data;
    const celldata = sheet.celldata || [];

    // Find dimensions
    let maxRow = 0, maxCol = 0;
    celldata.forEach(cell => {
      if (cell.r > maxRow) maxRow = cell.r;
      if (cell.c > maxCol) maxCol = cell.c;
    });

    // Build 2D array
    const grid = Array.from({ length: maxRow + 1 }, () => Array(maxCol + 1).fill(''));
    celldata.forEach(cell => {
      const val = cell.v?.v ?? cell.v?.m ?? cell.v ?? '';
      grid[cell.r][cell.c] = String(val);
    });

    // Convert to CSV
    const csv = grid.map(row =>
      row.map(cell => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return '"' + cell.replace(/"/g, '""') + '"';
        }
        return cell;
      }).join(',')
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(sheetTitle || 'spreadsheet').replace(/[^a-zA-Z0-9_-]/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Drag resize handlers (desktop only)
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [width]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const maxWidth = window.innerWidth * 0.8;
    const deltaX = startXRef.current - e.clientX;
    const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidthRef.current + deltaX));
    setWidth(newWidth);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!shouldRender) return null;

  // Evaluate formula from 2D data array
  const evalFormula = (formula, data) => {
    try {
      // Parse cell reference like "C2" → [row, col] (0-indexed)
      const parseRef = (ref) => {
        const m = ref.match(/^([A-Z]+)(\d+)$/i);
        if (!m) return null;
        let col = 0;
        for (const ch of m[1].toUpperCase()) col = col * 26 + (ch.charCodeAt(0) - 64);
        return [parseInt(m[2]) - 1, col - 1];
      };

      // Get cell value from data array
      const getCellVal = (ref) => {
        const pos = parseRef(ref);
        if (!pos) return 0;
        const val = data[pos[0]]?.[pos[1]];
        if (val === null || val === undefined || val === '') return 0;
        if (typeof val === 'number') return val;
        if (typeof val === 'string' && val.startsWith('=')) return evalFormula(val, data);
        const n = parseFloat(val);
        return isNaN(n) ? 0 : n;
      };

      // Expand range like "C2:C8" into array of values
      const expandRange = (range) => {
        const parts = range.split(':');
        if (parts.length !== 2) return [];
        const start = parseRef(parts[0]), end = parseRef(parts[1]);
        if (!start || !end) return [];
        const vals = [];
        for (let r = start[0]; r <= end[0]; r++) {
          for (let c = start[1]; c <= end[1]; c++) {
            const v = data[r]?.[c];
            if (v === null || v === undefined || v === '') continue;
            if (typeof v === 'string' && v.startsWith('=')) { vals.push(evalFormula(v, data)); continue; }
            const n = typeof v === 'number' ? v : parseFloat(v);
            if (!isNaN(n)) vals.push(n);
          }
        }
        return vals;
      };

      const f = formula.substring(1).trim(); // Remove "="

      // SUM(range)
      const sumMatch = f.match(/^SUM\((.+)\)$/i);
      if (sumMatch) {
        const vals = expandRange(sumMatch[1]);
        return vals.reduce((a, b) => a + b, 0);
      }

      // AVERAGE(range)
      const avgMatch = f.match(/^AVERAGE\((.+)\)$/i);
      if (avgMatch) {
        const vals = expandRange(avgMatch[1]);
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      }

      // MIN(range)
      const minMatch = f.match(/^MIN\((.+)\)$/i);
      if (minMatch) {
        const vals = expandRange(minMatch[1]);
        return vals.length ? Math.min(...vals) : 0;
      }

      // MAX(range)
      const maxMatch = f.match(/^MAX\((.+)\)$/i);
      if (maxMatch) {
        const vals = expandRange(maxMatch[1]);
        return vals.length ? Math.max(...vals) : 0;
      }

      // COUNT(range)
      const countMatch = f.match(/^COUNT\((.+)\)$/i);
      if (countMatch) {
        return expandRange(countMatch[1]).length;
      }

      // Simple arithmetic: replace cell refs with values, then eval
      const expr = f.replace(/[A-Z]+\d+/gi, (ref) => String(getCellVal(ref)));
      // Only allow numbers, operators, parentheses, decimal points, spaces
      if (/^[\d\s+\-*/().]+$/.test(expr)) {
        const result = Function('"use strict"; return (' + expr + ')')();
        return typeof result === 'number' && isFinite(result) ? result : null;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Convert AI data to Fortune Sheet format
  const getSheetConfig = () => {
    const data = latestDataRef.current;
    if (!data) return [{ name: 'Sheet1', celldata: [] }];

    // If already in Fortune Sheet format (array of sheets)
    if (Array.isArray(data) && data[0]?.celldata) return data;

    // If it's a simple 2D array (rows/columns from AI)
    if (Array.isArray(data) && Array.isArray(data[0])) {
      const celldata = [];
      const formulas = [];
      data.forEach((row, r) => {
        row.forEach((val, c) => {
          if (val !== null && val !== undefined && val !== '') {
            const cell = { r, c, v: {} };
            if (typeof val === 'string' && val.startsWith('=')) {
              // Formula cell - pre-compute value since Fortune Sheet won't auto-calc on load
              const computed = evalFormula(val, data);
              cell.v = { f: val, v: computed, ct: { fa: 'General', t: 'n' }, m: computed != null ? String(computed) : '' };
              formulas.push({ r, c, formula: val });
            } else if (typeof val === 'number') {
              cell.v = { v: val, ct: { fa: 'General', t: 'n' }, m: String(val) };
            } else {
              cell.v = { v: val, ct: { fa: 'General', t: 's' }, m: String(val) };
            }
            // Bold header row
            if (r === 0) {
              cell.v.bl = 1;
            }
            celldata.push(cell);
          }
        });
      });
      formulaCellsRef.current = formulas;
      return [{
        name: sheetTitle || 'Sheet1',
        celldata,
        row: Math.max(data.length + 10, 30),
        column: Math.max((data[0] || []).length + 5, 15),
        config: {}
      }];
    }

    return [{ name: 'Sheet1', celldata: [], row: 30, column: 15 }];
  };

  // Recalculate formula cells when user edits data
  const recalcFormulas = (sheetDataArr) => {
    if (!workbookApiRef.current || !formulaCellsRef.current.length) return;
    if (isRecalcRef.current) return;

    // Build 2D grid from Fortune Sheet's internal data
    const sheet = Array.isArray(sheetDataArr) ? sheetDataArr[0] : sheetDataArr;
    if (!sheet?.data) return;
    const grid = sheet.data; // Fortune Sheet's 2D data array

    // Extract plain values into simple 2D array for evalFormula
    const plainGrid = grid.map(row =>
      (row || []).map(cell => {
        if (!cell) return '';
        if (cell.f) return cell.f; // keep formula string
        return cell.v ?? '';
      })
    );

    isRecalcRef.current = true;
    try {
      formulaCellsRef.current.forEach(({ r, c, formula }) => {
        const newVal = evalFormula(formula, plainGrid);
        if (newVal == null) return;
        const currentCell = grid[r]?.[c];
        const currentVal = currentCell?.v;
        if (currentVal !== newVal) {
          try {
            workbookApiRef.current.setCellValue(r, c, { f: formula, v: newVal, m: String(newVal), ct: { fa: 'General', t: 'n' } });
          } catch (e) { /* ignore */ }
        }
      });
    } finally {
      isRecalcRef.current = false;
    }
  };

  const panelTransform = isTouch
    ? (isVisible ? 'translateY(0)' : 'translateY(100%)')
    : (isVisible ? 'translateX(0)' : 'translateX(100%)');

  return (
    <div
      className={`diagram-editor-panel ${isTouch ? 'diagram-editor-panel--mobile' : 'diagram-editor-panel--desktop'}`}
      style={{
        ...(!isTouch && { width: `${width}px` }),
        transform: panelTransform,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Drag Handle - desktop only */}
      {!isTouch && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-blue-500 transition-colors group"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-12 -ml-1.5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex gap-0.5">
              <ChevronLeft size={12} className="text-blue-500" />
              <ChevronRight size={12} className="text-blue-500" />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative flex items-center justify-between px-4 py-2 border-b"
        style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))', backgroundColor: 'var(--bg-secondary, #1e1e2e)' }}>
        <h3 className="font-medium truncate max-w-[50%] text-sm" style={{ color: 'var(--text-primary)' }} title={sheetTitle}>
          {sheetTitle}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={exportCsv}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-white/10 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            title="Export CSV"
          >
            <Download size={14} />
            CSV
          </button>
          <button
            onClick={close}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors ml-2"
            title="Close (Esc)"
          >
            <X size={18} style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>
      </div>

      {/* Fortune Sheet */}
      <div className="flex-1 overflow-hidden" ref={workbookRef} style={{ minHeight: 0, backgroundColor: '#fff' }}>
        <SheetErrorBoundary>
          <Workbook
            key={sheetKey}
            ref={workbookApiRef}
            data={getSheetConfig()}
            onChange={(data) => {
              latestDataRef.current = data;
              if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
              recalcTimerRef.current = setTimeout(() => recalcFormulas(data), 50);
            }}
            style={{ width: '100%', height: '100%' }}
          />
        </SheetErrorBoundary>
      </div>
    </div>
  );
};

export default SpreadsheetEditorPanel;
